import { HttpClient } from '@angular/common/http';
import { Injectable, signal, WritableSignal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { SignalType } from 'angular-to-phaser';
import { catchError, map, Observable, of, retry, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Signal, SpotlightEvent, SpotlightSignal } from '../dto';
import { AbstractService } from './abstractservice';
import { ApplicationService } from './application.service';
import { SignalService } from './signal.service';

const MAX_ACTIVE_SPOTLIGHTS = 10; // matches SpotlightService.GetActive's pool size on the backend

@Injectable({
    providedIn: 'root'
})
export class SpotlightService extends AbstractService {

    private static LAST_SEEN_KEY = 'SPOTLIGHT_LAST_SEEN_ID';

    private readonly activeSpotlightsState: WritableSignal<SpotlightEvent[]> = signal<SpotlightEvent[]>([]);
    public readonly activeSpotlights$: Observable<SpotlightEvent[]> = toObservable(this.activeSpotlightsState);

    constructor(coreAppService: ApplicationService, private httpClient: HttpClient, private signalService: SignalService) {
        super(coreAppService.authService, coreAppService.featureFlagService);
        this.refresh().subscribe();
        this.signalService.latestNotificationSignal.asObservable().subscribe(this.receiveSignal.bind(this));
    }

    /** Silent push from the server (SignalType.Spotlight) - just keeps the pool current, never toasts. */
    private receiveSignal(value: Signal): void {
        if (value?.SignalType !== SignalType.Spotlight) return;

        const newEvent = (<SpotlightSignal>value).SpotlightEvent;
        const pool = [newEvent, ...this.activeSpotlightsState().filter(e => e.Id !== newEvent.Id)];
        this.activeSpotlightsState.set(pool.slice(0, MAX_ACTIVE_SPOTLIGHTS));
    }

    public refresh(): Observable<SpotlightEvent[]> {
        if (this.featureFlagService.isFlagOpen('great-jorb', false)) {
            return this.httpClient.get<SpotlightEvent[]>(`${environment.apiUrl}/spotlight`, this.httpOptionsAuthJson())
                .pipe(
                    map(events => (events ?? []).map(e => new SpotlightEvent().map(e, null))),
                    tap(events => this.activeSpotlightsState.set(events)),
                    retry(2),
                    catchError(this.handleError)
                );
        }
        return of([]);
    }

    /** Newest-if-unseen, otherwise a random pick from the shared active pool - each browser tracks its own "last seen" flag. */
    public selectDisplayEvent(pool: SpotlightEvent[]): SpotlightEvent {
        if (!pool?.length) return null;

        const newest = pool[0]; // API orders CreatedUtc descending
        const lastSeenId = window.localStorage.getItem(SpotlightService.LAST_SEEN_KEY);
        if (newest.Id !== lastSeenId) {
            window.localStorage.setItem(SpotlightService.LAST_SEEN_KEY, newest.Id);
            return newest;
        }
        return pool[Math.floor(Math.random() * pool.length)];
    }
}
